/**
 * Presentational configuration for the Financial Position tab's empty state.
 *
 * Everything here is **static**: this screen reads nothing yet, so the "which
 * zone are we in" and "what is connected" answers are fixed rather than
 * derived. They live in one place so that when a real read arrives, the shapes
 * below are what it fills — the View never has to change to show live figures.
 */

/** The three zones the assessment can land in, in sequence order. */
export type ZoneId = 'strained' | 'stable' | 'strong';

/** Which way a zone's illustrative trend runs. Drives the sparkline and its colour. */
export type ZoneTrend = 'down' | 'flat' | 'up';

export type Zone = {
    id: ZoneId;
    trend: ZoneTrend;
    labelKey: string;
    captionKey: string;
};

/**
 * The zone sequence stays on screen while the assessment is pending, rather
 * than being replaced by the "no data" panel. Reading Strained → Stable →
 * Strong tells a new user what this screen will eventually say about them; a
 * bare notice would only say that it currently says nothing.
 */
export const ZONES: Zone[] = [
    {
        id: 'strained',
        trend: 'down',
        labelKey: 'analytics.financialPosition.zones.strained.label',
        captionKey: 'analytics.financialPosition.zones.strained.caption',
    },
    {
        id: 'stable',
        trend: 'flat',
        labelKey: 'analytics.financialPosition.zones.stable.label',
        captionKey: 'analytics.financialPosition.zones.stable.caption',
    },
    {
        id: 'strong',
        trend: 'up',
        labelKey: 'analytics.financialPosition.zones.strong.label',
        captionKey: 'analytics.financialPosition.zones.strong.caption',
    },
];

/** Polyline + arrowhead geometry per trend, drawn in a 96×40 box. */
export const ZONE_TREND_SHAPES: Record<
    ZoneTrend,
    { line: string; arrow: string }
> = {
    down: { line: '4,10 26,17 50,16 74,28', arrow: '68,26 78,31 74,21' },
    flat: { line: '4,24 28,18 52,23 76,20', arrow: '70,15 80,20 70,25' },
    up: { line: '4,32 28,26 52,28 76,10', arrow: '68,9 81,7 79,20' },
};

/**
 * What the assessment is still waiting for, in display order. Whether each step
 * is met is a **fact about the ledger** and comes from the read — a business has
 * "connected sales" when it has posted one, which is what the checklist is
 * really asking. Only the order and the wording live here.
 */
export type ReadinessId = 'sales' | 'expenses' | 'bank';

export type ReadinessStep = {
    /** Also the key into the read's `readiness` object. */
    id: ReadinessId;
    labelKey: string;
};

export const READINESS_STEPS: ReadinessStep[] = [
    { id: 'sales', labelKey: 'analytics.financialPosition.pending.sales' },
    {
        id: 'expenses',
        labelKey: 'analytics.financialPosition.pending.expenses',
    },
    { id: 'bank', labelKey: 'analytics.financialPosition.pending.bank' },
];

/**
 * A balance-sheet section with nothing in it. Unlike a prompt, there is no
 * action to offer — the section fills itself as a side effect of ordinary work
 * elsewhere — so it states where that work happens instead of pointing at a
 * button that does not exist.
 */
export type NoticeId = 'inventory' | 'loans';

export type Notice = {
    id: NoticeId;
    icon: string;
    /** Alert status: `success` for a section whose emptiness is good news. */
    status: 'default' | 'success';
    titleKey: string;
    questionKey: string;
    noticeKey: string;
};

export const NOTICES: Notice[] = [
    {
        id: 'inventory',
        icon: 'solar:box-bold-duotone',
        status: 'default',
        titleKey: 'analytics.financialPosition.inventory.title',
        questionKey: 'analytics.financialPosition.inventory.question',
        noticeKey: 'analytics.financialPosition.inventory.notice',
    },
    {
        id: 'loans',
        // No active loans is not a gap to fill — it is a clean balance sheet,
        // which is why this one is green while the inventory notice is grey.
        icon: 'solar:card-bold-duotone',
        status: 'success',
        titleKey: 'analytics.financialPosition.loans.title',
        questionKey: 'analytics.financialPosition.loans.question',
        noticeKey: 'analytics.financialPosition.loans.notice',
    },
];
